import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, Clock } from "lucide-react";

const contactInfo = [
  {
    icon: Phone,
    title: "Telefon",
    value: "0761 712 940",
    href: "tel:0761712940",
  },
  {
    icon: Mail,
    title: "Email",
    value: "l.esko@yahoo.com",
    href: "mailto:l.esko@yahoo.com",
  },
  {
    icon: MapPin,
    title: "Adresa",
    value: "Râmnicu Sărat, Județul Buzău",
    href: null,
  },
  {
    icon: Clock,
    title: "Program",
    value: "Luni - Sâmbătă: 09:00 - 18:00",
    href: null,
  },
];

const serviceOptions = [
  "Detailing Interior",
  "Detailing Exterior",
  "Detailing Complet",
  "Polish Auto",
  "Protecție Ceramică",
  "Colantări Auto",
  "Folie PPF",
  "Folie Geamuri",
  "Spălare Premium",
  "Altele",
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(
      `Cerere Ofertă LSK - ${formData.service || "General"}`
    );
    const body = encodeURIComponent(
      `Nume: ${formData.name}\nTelefon: ${formData.phone}\nEmail: ${formData.email}\nServiciu dorit: ${formData.service}\n\nMesaj:\n${formData.message}`
    );
    window.location.href = `mailto:l.esko@yahoo.com?subject=${subject}&body=${body}`;
  };

  return (
    <div className="pt-20 min-h-screen bg-black" data-testid="contact-page">
      {/* Header */}
      <section className="py-20 page-header-bg">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.h1
            className="text-4xl sm:text-5xl lg:text-6xl mb-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-red-600">Contact</span>
          </motion.h1>
          <motion.div
            className="red-line mx-auto mb-6"
            initial={{ width: 0 }}
            animate={{ width: 60 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          />
          <motion.p
            className="text-base md:text-lg font-light max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            style={{ fontFamily: "'Raleway', sans-serif" }}
          >
            Suntem aici pentru tine. Contactează-ne pentru o ofertă personalizată.
          </motion.p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, i) => (
              <motion.div
                key={info.title}
                className="shine-card p-6 bg-[#0A0A0A] border border-[#222] hover:border-red-600 transition-all duration-500 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                data-testid={`contact-info-${info.title.toLowerCase()}`}
              >
                <info.icon className="w-10 h-10 text-red-600 mx-auto mb-4" />
                <h3 className="text-lg mb-2">{info.title}</h3>
                {info.href ? (
                  <a
                    href={info.href}
                    className="text-white text-sm font-light hover:text-red-500 transition-colors"
                    style={{ fontFamily: "'Raleway', sans-serif" }}
                  >
                    {info.value}
                  </a>
                ) : (
                  <p
                    className="text-white text-sm font-light"
                    style={{ fontFamily: "'Raleway', sans-serif" }}
                  >
                    {info.value}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form + Map */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl mb-6">
              Cerere de <span className="text-red-600">Oferta</span>
            </h2>
            <div className="red-line mb-8" />

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div>
                <label
                  className="block text-white text-sm mb-2 tracking-wide font-medium"
                  style={{ fontFamily: "'Raleway', sans-serif" }}
                >
                  Nume Complet
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="contact-name"
                  className="w-full bg-[#0A0A0A] border border-[#333] text-white p-3 text-sm focus:border-red-600 outline-none transition-colors"
                  style={{ fontFamily: "'Raleway', sans-serif" }}
                  placeholder="Numele tău"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label
                    className="block text-white text-sm mb-2 tracking-wide font-medium"
                    style={{ fontFamily: "'Raleway', sans-serif" }}
                  >
                    Telefon
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="contact-phone"
                    className="w-full bg-[#0A0A0A] border border-[#333] text-white p-3 text-sm focus:border-red-600 outline-none transition-colors"
                    style={{ fontFamily: "'Raleway', sans-serif" }}
                    placeholder="0712 345 678"
                  />
                </div>
                <div>
                  <label
                    className="block text-white text-sm mb-2 tracking-wide font-medium"
                    style={{ fontFamily: "'Raleway', sans-serif" }}
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    data-testid="contact-email"
                    className="w-full bg-[#0A0A0A] border border-[#333] text-white p-3 text-sm focus:border-red-600 outline-none transition-colors"
                    style={{ fontFamily: "'Raleway', sans-serif" }}
                    placeholder="email@exemplu.com"
                  />
                </div>
              </div>

              <div>
                <label
                  className="block text-white text-sm mb-2 tracking-wide font-medium"
                  style={{ fontFamily: "'Raleway', sans-serif" }}
                >
                  Serviciu Dorit
                </label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  data-testid="contact-service"
                  className="w-full bg-[#0A0A0A] border border-[#333] text-white p-3 text-sm focus:border-red-600 outline-none transition-colors"
                  style={{ fontFamily: "'Raleway', sans-serif" }}
                >
                  <option value="">Selectează un serviciu</option>
                  {serviceOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className="block text-white text-sm mb-2 tracking-wide font-medium"
                  style={{ fontFamily: "'Raleway', sans-serif" }}
                >
                  Mesaj
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  data-testid="contact-message"
                  className="w-full bg-[#0A0A0A] border border-[#333] text-white p-3 text-sm focus:border-red-600 outline-none transition-colors resize-none"
                  style={{ fontFamily: "'Raleway', sans-serif" }}
                  placeholder="Descrie ce ai dori..."
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit-btn"
                className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-red-600 text-white hover:bg-red-700 hover:shadow-[0_0_30px_rgba(220,38,38,0.4)] transition-all duration-300 tracking-[0.15em] uppercase text-sm font-medium"
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                <Send className="w-4 h-4" />
                Trimite Cererea
              </button>
            </form>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="flex flex-col"
          >
            <h2 className="text-3xl sm:text-4xl mb-6">
              Ne <span className="text-red-600">Gasesti</span> Aici
            </h2>
            <div className="red-line mb-8" />
            <div className="flex-1 min-h-[400px] border border-[#222]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d44928.34683920619!2d26.80799!3d45.38333!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x40b2580c3a6f0c63%3A0x3c74b8e3c7c37cce!2sR%C3%A2mnicu%20S%C4%83rat!5e0!3m2!1sro!2sro"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: "400px" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="LSK Auto Styling - Râmnicu Sărat"
                data-testid="contact-map"
              />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
